// src/features/portfolio-optimization/components/SectorDonut.tsx
//
// Sector-exposure donut for the active risk profile. Rolls the per-asset
// target weights up into sector buckets and renders a Recharts donut + a
// sorted legend with the exact percentages. Built to match the design
// language of the existing stock-ranking charts (dark-mode aware tooltip,
// brand-neutral slate scaffolding, recharts).

import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';

import { sectorColor } from './sectorColors';

export interface SectorSlice {
    name: string;
    /** Percent of the book (0..100). */
    value: number;
}

interface SectorDonutProps {
    data: SectorSlice[];
}

const DonutTooltip = ({
    active,
    payload,
}: {
    active?: boolean;
    payload?: { name: string; value: number; payload: SectorSlice }[];
}) => {
    if (!active || !payload?.length) return null;
    const slice = payload[0];
    return (
        <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-xs shadow-lg">
            <p className="font-bold text-slate-900 dark:text-white">{slice.name}</p>
            <p className="font-mono text-slate-600 dark:text-slate-300">
                {slice.value.toFixed(2)}%
            </p>
        </div>
    );
};

export const SectorDonut = ({ data }: SectorDonutProps) => {
    if (!data.length) {
        return (
            <p className="muted text-sm">No sector exposure to display.</p>
        );
    }

    return (
        <div className="flex flex-col">
            <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                        <Pie
                            data={data}
                            cx="50%"
                            cy="50%"
                            innerRadius={70}
                            outerRadius={95}
                            paddingAngle={2}
                            dataKey="value"
                            stroke="none"
                        >
                            {data.map((slice) => (
                                <Cell
                                    key={slice.name}
                                    fill={sectorColor(slice.name)}
                                />
                            ))}
                        </Pie>
                        <Tooltip content={<DonutTooltip />} />
                    </PieChart>
                </ResponsiveContainer>
            </div>

            <div className="mt-4 space-y-2.5 overflow-y-auto max-h-64 pr-2">
                {data.map((slice) => (
                    <div
                        key={slice.name}
                        className="flex justify-between items-center text-sm"
                    >
                        <div className="flex items-center gap-2 min-w-0">
                            <span
                                className="w-3 h-3 rounded-full shrink-0"
                                style={{ backgroundColor: sectorColor(slice.name) }}
                            />
                            <span className="text-slate-600 dark:text-slate-300 truncate max-w-[160px] font-medium">
                                {slice.name}
                            </span>
                        </div>
                        <span className="font-bold text-slate-900 dark:text-white font-mono">
                            {slice.value.toFixed(1)}%
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default SectorDonut;
